import { TimesheetState } from '../timesheet-collection.state';
import { TimesheetRow } from './build-timesheet-rows';

/**
 * The editable projection of a timesheet — what the signal form holds and what an edit sends
 * back.
 *
 * Two shape decisions worth knowing:
 *
 * - **Every value is shaped as the slice shapes it** — an instant is one ISO string, a choice is
 *   the chosen code. What the inputs below exchange (a date and a time, a collection of one) is
 *   `pace-instant-field`'s and `pace-select-field`'s business, not the model's.
 * - **An edit carries patches, not whole entries.** The form edits a handful of fields per
 *   entry; the rest (work code, distance, notification, …) it never touches. Sending patches
 *   keyed by `entryId` means the reducer merges rather than replaces, so a field the form does
 *   not know about cannot be lost by omission. Adding and deleting entries is structural and
 *   travels as its own action.
 */

/** One editable entry of the form. */
export type TimesheetFormEntry = {
    entryId: string;
    kind: 'work' | 'manual';
    /** ISO instant, or '' while unset */
    start: string;
    /** ISO instant, or '' while unset */
    end: string;
    /** the chosen gap task type, always empty on a work entry */
    taskType: string;
    /**
     * The entry's work code.
     *
     * Only editable on a travel-back entry the page unlocks, where it switches the leg between
     * billable and standby; everywhere else it round-trips untouched.
     */
    workCode: string;
    /**
     * The chosen means of transport. Editable on an unlocked travel-back entry; elsewhere it
     * round-trips or is simply displayed.
     */
    meansOfTransport: string;
    /** the chosen address; `null` until one is picked. Work entries bring theirs from the confirmation. */
    address: string | null;
    /**
     * Whether the owning confirmation is still in progress. Not edited — the schema reads it to
     * make the row read-only, since the confirmation still owns those times.
     */
    inProgress: boolean;
    /**
     * Whether this is the travel-back leg the timesheet may still speak for — the last settled
     * one. Not edited: the schema reads it to unlock the entry's end, since the journey home is
     * the one thing a confirmation records before it is over.
     */
    isTravelBackEditable: boolean;
    /**
     * Whether this is the travel-to leg the timesheet may still speak for — the first settled
     * one. Not edited: the schema reads it to unlock the entry's start, since the journey to the
     * customer is the one thing a confirmation records before it has begun.
     */
    isTravelToEditable: boolean;
};

export type TimesheetFormModel = {
    /** which timesheet this model projects, so an edit cannot land on the wrong one */
    timesheetId: string;
    /** ISO instant, or '' while unset */
    startOfWork: string;
    /** ISO instant, or '' while unset */
    endOfWork: string;
    entries: TimesheetFormEntry[];
};

/** The model a page binds to while no timesheet is available to edit. */
export const emptyFormModel: TimesheetFormModel = {
    timesheetId: '',
    startOfWork: '',
    endOfWork: '',
    entries: [],
};

/** The fields an edit writes back for one entry. */
export type TimesheetEntryPatch = {
    entryId: string;
    /** ISO */
    start: string;
    /** ISO */
    end: string;
    /** omitted for a work entry, whose task type is not editable */
    taskType?: string;
    /** omitted for a manual entry, whose kind is carried by its task type and travel type */
    workCode?: string;
    /** omitted for a manual entry, which records no means of transport */
    meansOfTransport?: string;
    /** omitted for a work entry, whose address comes from the confirmation */
    address?: string | null;
};

/**
 * Builds the editable model for a timesheet from its projected rows.
 *
 * Gap rows are left out: a gap is the absence of an entry, so there is nothing to edit on it —
 * the user closes one by adding a manual entry, which is a structural change.
 *
 * @param timesheet the timesheet being edited
 * @param rows its projected rows
 * @returns the model the signal form edits
 */
export function toTimesheetFormModel(timesheet: TimesheetState, rows: TimesheetRow[]): TimesheetFormModel {
    const lastRow = rows.at(-1);
    return {
        timesheetId: timesheet.timesheetId,
        startOfWork: timesheet.startOfWork,
        // the projection resolves an unset end of work onto the last entry, and that resolved
        // instant is the one on show, so it is the one the form edits
        endOfWork: lastRow?.kind === 'eow' ? lastRow.end : timesheet.endOfWork,
        entries: rows
            .filter((row) => row.kind === 'work' || row.kind === 'manual')
            .map((row) => ({
                entryId: row.entryId,
                kind: row.kind,
                start: row.start,
                end: row.end,
                taskType: row.kind === 'manual' ? row.taskType : '',
                workCode: row.kind === 'work' ? row.workCode : '',
                meansOfTransport: row.kind === 'work' ? row.meansOfTransport : '',
                address: row.address,
                inProgress: row.kind === 'work' ? row.inProgress : false,
                isTravelBackEditable: row.kind === 'work' ? row.isTravelBackEditable : false,
                isTravelToEditable: row.kind === 'work' ? row.isTravelToEditable : false,
            })),
    };
}

/**
 * Turns the model's entries into the patches an edit carries.
 *
 * A work entry contributes its times plus the two codes a travel-back leg can change; its task
 * type is not editable and its address belongs to the confirmation that recorded it.
 *
 * Rows of an open confirmation contribute nothing. The schema renders them read-only, so their patch
 * could only ever restate what the row already held — and once those rows are derived from the
 * confirmation slice rather than stored, no entry answers to their id, so the commit would drop each
 * patch and report it as an error on every save.
 *
 * @param model the edited model
 * @returns one patch per editable entry, in model order
 */
export function toEntryPatches(model: TimesheetFormModel): TimesheetEntryPatch[] {
    return model.entries
        .filter((entry) => !entry.inProgress)
        .map((entry) => ({
            entryId: entry.entryId,
            start: entry.start,
            end: entry.end,
            ...(entry.kind === 'manual'
                ? { taskType: entry.taskType, address: entry.address }
                : { workCode: entry.workCode, meansOfTransport: entry.meansOfTransport }),
        }));
}

/**
 * Whether `later` lies strictly after `earlier`, as ISO instants. Unparsable instants never
 * compare as later, so an incomplete edit does not move the end of work on a guess.
 *
 * @param earlier the earlier instant
 * @param later the later instant
 * @returns whether there is a positive span between the two
 */
const isLater = (earlier: string, later: string): boolean => {
    const from = Date.parse(earlier);
    const to = Date.parse(later);
    return !Number.isNaN(from) && !Number.isNaN(to) && to > from;
};

/**
 * Moves the end of work out to the travel-back leg when that leg now ends later.
 *
 * The journey home is the last thing in the day, so extending it extends the day. Without this the
 * containment rule would refuse the edit and the technician would have to move the end of work
 * first, then the leg — which is the order nobody thinks in.
 *
 * Only outwards. Pulling the leg back leaves the end of work where it was, which shows up as a gap
 * the technician can close with the button already there.
 *
 * @param model the model as the form holds it
 * @returns the model with the end of work moved out, or the same model when nothing moved
 */
export function withEndOfWorkFollowingTravelBack(model: TimesheetFormModel): TimesheetFormModel {
    const leg = model.entries.find((entry) => entry.isTravelBackEditable && !entry.inProgress);
    if (!leg?.end || !isLater(model.endOfWork, leg.end)) {
        return model;
    }
    return { ...model, endOfWork: leg.end };
}

/**
 * Moves the start of work back to the travel-to leg when that leg now starts earlier.
 *
 * The journey to the customer is the first thing in the day, so pulling it earlier extends the day
 * backwards. Without this the containment rule would refuse the edit and the technician would have
 * to move the start of work first, then the leg — which is the order nobody thinks in.
 *
 * Only backwards. Pushing the leg later leaves the start of work where it was, which shows up as a
 * gap the technician can close with a manual entry.
 *
 * @param model the model as the form holds it
 * @returns the model with the start of work moved back, or the same model when nothing moved
 */
export function withStartOfWorkPrecedingTravelTo(model: TimesheetFormModel): TimesheetFormModel {
    const leg = model.entries.find((entry) => entry.isTravelToEditable && !entry.inProgress);
    if (!leg?.start || !isLater(leg.start, model.startOfWork)) {
        return model;
    }
    return { ...model, startOfWork: leg.start };
}
